import type { ReactNode } from "react";
import { ApiError } from "../lib/api.ts";
import { useOnline } from "../lib/useApi.ts";
import { Banner } from "./feedback.tsx";
import { EmptyState } from "./surfaces.tsx";

/** Plain-language reason for a failed request (DESIGN.md §8 wording). */
export function reason(e: Error): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return "You don’t have permission to see this. Ask the trip admin.";
    if (e.status === 404) return "This isn’t here, or you are not a member of the trip.";
    if (e.status === 429) return "Too many requests. Wait a moment and try again.";
    if (e.status >= 500) return "The server had a problem. Your data is safe; try again.";
    return e.message;
  }
  return "We couldn’t reach the server. Check your connection.";
}

/**
 * The states every page defines: loading (a skeleton with final dimensions), offline, failure with
 * retry, permission denied, and empty. Content stays visible during a reload or after a failure.
 */
export function PageState<T>({
  data,
  error,
  loading,
  reload,
  isEmpty,
  empty,
  skeletonRows = 3,
  children,
}: {
  data: T | null;
  error: Error | null;
  loading: boolean;
  reload: () => void;
  isEmpty?: (d: T) => boolean;
  empty?: ReactNode;
  skeletonRows?: number;
  children: (d: T) => ReactNode;
}) {
  const online = useOnline();
  const banner = !online ? (
    <Banner tone="wait" message="You are offline. What you see may be out of date." />
  ) : error ? (
    <Banner
      tone="bad"
      message={reason(error)}
      {...(error instanceof ApiError && error.status === 403
        ? {}
        : { action: { label: "Retry", onClick: reload } })}
    />
  ) : null;
  if (data === null) {
    if (error || !online) return banner;
    if (loading)
      return (
        <div className="skeleton-list" aria-busy="true" aria-label="Loading">
          {Array.from({ length: skeletonRows }, (_, i) => (
            <div key={i} className="skeleton" />
          ))}
        </div>
      );
    return null;
  }
  return (
    <>
      {banner}
      {isEmpty?.(data) ? (empty ?? <EmptyState title="Nothing here yet" />) : children(data)}
    </>
  );
}
